import { Component, lazy } from "react";

/* Route-level code splitting (speed brief item 5, 2026-11-03).

   Pages that most shoppers never open (admin, the seller portal, checkout,
   the inbox, account pages, policy pages) are loaded only when visited, so
   a shopper's phone does not download them with the homepage.

   The catch with split code: a tab opened before a deploy still runs the
   old page, whose chunk files no longer exist after the deploy. Opening a
   lazy page then fails with a ChunkLoadError. The fix is one full reload,
   which fetches the new index.html and the new chunk names.

   One reload only. A sessionStorage flag records that we already reloaded;
   if the chunk fails again (the site is genuinely unreachable), the error
   goes to RouteChunkBoundary below, which shows a message instead of
   reloading forever. The flag clears as soon as any lazy chunk loads. */
export const RELOAD_FLAG = "emporia:chunk-reload";

export function isChunkLoadError(error) {
  if (!error) return false;
  const text = `${error.name || ""} ${error.message || ""}`;
  return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(text);
}

function readFlag() {
  try { return window.sessionStorage.getItem(RELOAD_FLAG) === "1"; } catch { return false; }
}

function writeFlag(on) {
  try {
    if (on) window.sessionStorage.setItem(RELOAD_FLAG, "1");
    else window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch { /* storage blocked: at worst no reload guard, and no reload either */ }
}

/* Exported for tests; `reload` is injectable because jsdom cannot reload. */
export function loadWithReload(factory, reload = () => window.location.reload()) {
  return factory().then(
    (module) => { writeFlag(false); return module; },
    (error) => {
      if (isChunkLoadError(error) && !readFlag()) {
        writeFlag(true);
        reload();
        // Never settle: the page is about to be replaced.
        return new Promise(() => {});
      }
      throw error;
    },
  );
}

export default function lazyWithReload(factory) {
  return lazy(() => loadWithReload(factory));
}

/* Shown when a page's code still cannot load after the one reload. */
export class RouteChunkBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { failed: true, error };
  }

  componentDidUpdate(prevProps) {
    // A new page gets a fresh chance.
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false, error: null });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    // Only a page whose code could not load is handled here; any other
    // error carries on exactly as it did before code splitting.
    if (!isChunkLoadError(this.state.error)) throw this.state.error;
    return (
      <div role="alert" className="route-chunk-error">
        <p>This page couldn't load. Check your connection and try again.</p>
        <button type="button" onClick={() => { writeFlag(false); window.location.reload(); }}>
          Try again
        </button>
      </div>
    );
  }
}

/* For a side widget on every page (Soko's chat button), never a page.
   Found live after #235 (2026-11-04): Soko was loaded through
   lazyWithReload and sat outside RouteChunkBoundary, so one failed
   download of its file reloaded the page the shopper was reading, and a
   second one reached no boundary and React unmounted the whole site (a
   blank page, home included). A widget whose code cannot load is simply
   left out: it renders nothing, the page stays, and nothing reloads. */
export class QuietChunkBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
